import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { BarGrid } from '@/components/charts/BarGrid'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Helper to get concern color from config
const getConcernColor = (label: string) => getResponseColor('concern', label)

// Map question IDs to full labels (from topline Q7 - exact poll wording)
const CONCERN_LABELS: Record<string, string> = {
  'Concern_WaterPollution': 'Pollution of lakes, rivers, and streams',
  'Concern_WildernessLoss': 'Loss of wilderness',
  'Concern_HabitatLoss': 'Loss of natural habitat for birds, fish and animals',
  'Concern_ForestLoss': 'Loss of forests',
  'Concern_WaterScarcity': 'Water scarcity and drought',
  'Concern_Wildfires': 'Wildfires',
  'Concern_ExtremeWeather': 'Extreme weather, including floods, storms, and heat waves',
  'Concern_ToxicChemicals': 'Toxic chemicals',
  'Concern_AirPollution': 'Air pollution',
  'Concern_AirPollutionHealth': 'Health impacts of air pollution',
  'Concern_TankerSpills': 'Oil tanker spills',
  'Concern_PipelineSpills': 'Oil pipeline spills',
  'Concern_EcosystemDisrupt': 'Disruption of natural ecosystems',
  'Concern_FoodChain': 'Disruption of the natural food chain',
  'Concern_PestsDisease': 'Pests and disease',
  'Concern_PesticideSpray': 'Pesticide spraying',
  'Concern_NaturalGasDev': 'Natural gas development',
  'Concern_Fracking': 'Fracking',
  'Concern_GMOs': 'Genetically modified organisms (GMOs)',
}

// Response order: most concerned to least (highest affirmative on left)
const CONCERN_RESPONSE_ORDER = ['Extremely Concerned', 'Very Concerned', 'Somewhat Concerned', 'Not Very Concerned', 'Not Concerned']

// Sort responses according to concern order
const sortConcernResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = CONCERN_RESPONSE_ORDER.indexOf(a.label)
    const bIdx = CONCERN_RESPONSE_ORDER.indexOf(b.label)
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

// Open-ended: Environmental Threats Over Next 10 Years (Q3 from topline)
const ENV_THREATS_QUESTION = {
  id: 'OpenEnded_EnvThreats10Yr',
  title: 'Environmental Threats Over Next 10 Years (UNAIDED)',
  text: 'Thinking of threats to the natural environment in your region over the next 10 years, what concerns you most, if anything?',
}

const ENV_THREATS_CONFIG: Array<{ label: string; shortLabel?: string; color: string }> = [
  { label: 'Climate change', shortLabel: 'Climate', color: '#7a0c0c' },
  { label: 'Extreme weather', shortLabel: 'Weather', color: '#a01a1c' },
  { label: 'Pollution in general', shortLabel: 'Pollution', color: '#4a3f35' },
  { label: 'Water pollution', shortLabel: 'Water', color: '#6b5b4f' },
  { label: 'Air pollution', shortLabel: 'Air', color: '#8c7b6f' },
  { label: 'Deforestation and forest fires', shortLabel: 'Forests', color: '#0f2d1a' },
  { label: 'Loss of biodiversity', shortLabel: 'Biodiversity', color: '#1a472a' },
  { label: 'Overdevelopment/urban sprawl', shortLabel: 'Sprawl', color: '#2d6a2f' },
  { label: 'Industrial activities', shortLabel: 'Industry', color: '#1e3a5f' },
  { label: 'Resource scarcity', shortLabel: 'Resources', color: '#2c5985' },
  { label: 'Population growth', shortLabel: 'Population', color: '#4e79a7' },
  { label: 'Government policy', shortLabel: 'Policy', color: '#5c2d51' },
  { label: 'Lack of respect for environment', shortLabel: 'Disrespect', color: '#7b4173' },
  { label: 'Skepticism/lack of concern', shortLabel: 'Skepticism', color: '#6b7280' },
]

export function EnvironmentalConcerns() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Get concern questions, sorted by highest concern level
  const concernQuestions = data
    ? Object.entries(data.questions)
        .filter(([id]) => id.startsWith('Concern_'))
        .map(([id, question]) => {
          // Calculate concern score for sorting (sum of Extremely + Very concerned)
          const filterKey = buildFilterKey('total', 'total')
          const questionData = question.data[filterKey]
          const extremely = questionData?.responses.find(r => r.label === 'Extremely Concerned')?.percentage || 0
          const very = questionData?.responses.find(r => r.label === 'Very Concerned')?.percentage || 0
          return { id, question, concernScore: extremely + very }
        })
        .sort((a, b) => b.concernScore - a.concernScore) // Sort by highest concern first
        .map(({ id, question }) => [id, question] as const)
    : []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Environmental Concerns
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-4">
          How concerned are you about the following environmental issues in your province?
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {CONCERN_RESPONSE_ORDER.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getConcernColor(label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading concern questions...</p>
          ) : isBreakActive ? (
            // Break is active - show multiple rows per question
            concernQuestions.map(([questionId, question]) => {
              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, value, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, value)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              const questionLabel = CONCERN_LABELS[questionId] || question.shortText || questionId.replace('Concern_', '').replace(/_/g, ' ')

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{questionLabel}</h3>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={getDisplayName(row.value)}>
                          {getDisplayName(row.value)}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortConcernResponses(row.data!.responses)}
                            scaleType="concern"
                            height={24}
                            showLabels={false}
                            normalize={true}
                            colorGetter={(label) => getConcernColor(label) || '#bab0ac'}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          ) : (
            // Total (no break) - show single bar per question with full labels
            concernQuestions.map(([questionId, question]) => {
              const filterKey = buildFilterKey('total', 'total')
              const questionData = question.data[filterKey]

              if (!questionData) return null

              const questionLabel = CONCERN_LABELS[questionId] || question.shortText || questionId.replace('Concern_', '').replace(/_/g, ' ')

              return (
                <div key={questionId} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0">
                  <HorizontalStackedBar
                    segments={sortConcernResponses(questionData.responses)}
                    scaleType="concern"
                    questionText={questionLabel}
                    sampleSize={questionData.sampleSize}
                    height={32}
                    showLabels={false}
                    colorGetter={(label) => getConcernColor(label) || '#bab0ac'}
                  />
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Open-ended: Environmental Threats Over Next 10 Years */}
      {data && (() => {
        const question = data.questions[ENV_THREATS_QUESTION.id]
        if (!question) return null

        const sampleData = question.data[buildFilterKey('total', 'total')]
        const dataLabels = new Set(sampleData?.responses?.map(r => r.label) || [])

        const columns = ENV_THREATS_CONFIG
          .filter(c => dataLabels.has(c.label))
          .map(c => ({
            id: c.label,
            label: c.label,
            shortLabel: c.shortLabel,
            color: c.color,
          }))

        const rows = breakValues.map(value => {
          const filterKey = buildFilterKey(
            'total',
            selectedBreak,
            isBreakActive ? value : undefined,
            selectedProvince
          )
          let questionData = question.data[filterKey]

          if (!questionData && selectedProvince) {
            const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
            questionData = question.data[fallbackKey]
          }

          if (!questionData) return null

          const values: Record<string, number> = {}
          questionData.responses.forEach(r => {
            values[r.label] = r.percentage
          })

          return {
            id: value,
            label: isBreakActive ? getDisplayName(value) : 'National Sample',
            values,
          }
        }).filter((row): row is NonNullable<typeof row> => row !== null)

        if (rows.length === 0 || columns.length === 0) return null

        return (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
            <BarGrid
              title={ENV_THREATS_QUESTION.title}
              description={ENV_THREATS_QUESTION.text}
              columns={columns}
              rows={rows}
              colorIntensity={false}
            />
          </div>
        )
      })()}
    </div>
  )
}
